import React from "react";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "15 February 2026";
const CONTROLLER = "Henry Hoe (Freelance Developer, Singapore)";
const CONTACT_EMAIL = "info@leadifycrm.com";

export default function Terms() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2" data-testid="terms-logo-link">
            <div className="h-8 w-8 rounded-sm bg-primary flex items-center justify-center font-black text-black">L</div>
            <div className="font-display font-black text-lg">LeadifyCRM</div>
          </Link>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="text-muted-foreground hover:text-white" data-testid="link-privacy">Privacy</Link>
            <Link to="/terms" className="text-primary" data-testid="link-terms">Terms</Link>
            <Link to="/login" className="text-muted-foreground hover:text-white">Sign in</Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8" data-testid="terms-page">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Legal</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black mt-1">Terms of Service</h1>
          <p className="text-sm text-muted-foreground mt-2">Effective {EFFECTIVE_DATE}</p>
        </div>

        <Section title="1. Acceptance of Terms">
          <p>
            By creating an account or using LeadifyCRM (the "Service"), you agree to be bound by these Terms of
            Service ("Terms"). If you do not agree, do not use the Service. The Service is provided by {CONTROLLER}.
          </p>
        </Section>

        <Section title="2. Description of Service">
          <p>
            LeadifyCRM is a multi-tenant customer relationship management (CRM) platform providing lead
            qualification, unified inbox across chat/email/WhatsApp/Facebook/Instagram, sales pipeline management,
            meeting scheduling, project tasks, analytics, and related features. Features may be added or removed
            at our discretion.
          </p>
        </Section>

        <Section title="3. Accounts">
          <ul className="space-y-1 list-disc ml-5">
            <li>You are responsible for maintaining the confidentiality of your credentials.</li>
            <li>You are responsible for all activity that occurs under your account.</li>
            <li>You must promptly notify us of any unauthorised access at <a className="text-primary underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.</li>
            <li>You must be at least 18 years old to create an account.</li>
          </ul>
        </Section>

        <Section title="4. Acceptable Use">
          <p>You agree NOT to:</p>
          <ul className="space-y-1 list-disc ml-5">
            <li>Use the Service for any unlawful, harmful, or fraudulent purpose</li>
            <li>Send unsolicited bulk messages (spam) through connected channels</li>
            <li>Reverse-engineer, decompile, or attempt to extract source code</li>
            <li>Interfere with or circumvent the Service's security features</li>
            <li>Upload malware or any harmful content</li>
            <li>Impersonate any person or entity, or misrepresent your affiliation</li>
            <li>Violate the terms of any third-party service integrated with LeadifyCRM (Meta, Google, etc.)</li>
          </ul>
        </Section>

        <Section title="5. Third-party integrations">
          <p>
            LeadifyCRM connects to third-party services (Facebook Messenger, Instagram, WhatsApp, Google Calendar,
            SMTP, IMAP, and LLM providers) at your election. You are responsible for complying with the terms of
            those third-party services and for the accuracy of any credentials you provide. We are not liable for
            any actions, downtime, or data loss by those third parties.
          </p>
        </Section>

        <Section title="6. User Content & Data">
          <p>
            You retain all rights to the content you upload or route through the Service ("Customer Data"). You
            grant us a limited, non-exclusive licence to process Customer Data solely for the purpose of providing
            the Service. We do not use Customer Data to train shared machine-learning models or for advertising.
          </p>
          <p>You represent and warrant that you have all rights necessary to submit Customer Data to the Service.</p>
        </Section>

        <Section title="7. Fees & Payment">
          <p>
            Unless a separate written agreement states otherwise, the Service is currently offered on a free-of-charge
            basis. Paid plans may be introduced later; we will notify you at least 30 days in advance before any charges
            take effect.
          </p>
        </Section>

        <Section title="8. Termination">
          <p>
            You may stop using the Service at any time. We may suspend or terminate your account without notice if
            you breach these Terms or if we believe your usage puts the Service or its users at risk. Upon
            termination, your right to access the Service ceases immediately; you may request an export of your
            data by emailing <a className="text-primary underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> within 30 days of termination.
          </p>
        </Section>

        <Section title="9. Disclaimers">
          <p>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED,
            INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
          </p>
        </Section>

        <Section title="10. Limitation of Liability">
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {CONTROLLER.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT,
            INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUE, WHETHER
            INCURRED DIRECTLY OR INDIRECTLY, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY IN ANY CASE
            SHALL NOT EXCEED SGD 100.
          </p>
        </Section>

        <Section title="11. Indemnification">
          <p>
            You agree to indemnify and hold harmless {CONTROLLER} from any claims, damages, or expenses arising
            from (a) your breach of these Terms, (b) your Customer Data, or (c) your violation of any law or
            third-party right.
          </p>
        </Section>

        <Section title="12. Governing Law">
          <p>
            These Terms are governed by the laws of Singapore. Any disputes shall be resolved exclusively in the
            courts of Singapore.
          </p>
        </Section>

        <Section title="13. Changes to these Terms">
          <p>
            We may revise these Terms from time to time. Material changes will be announced at least 14 days in
            advance. Continued use of the Service after changes take effect constitutes acceptance.
          </p>
        </Section>

        <Section title="14. Contact">
          <p>
            {CONTROLLER}
            <br />Email: <a className="text-primary underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
        </Section>
      </main>

      <footer className="border-t border-border mt-8">
        <div className="max-w-4xl mx-auto px-6 py-6 text-xs text-muted-foreground flex flex-wrap justify-between gap-2">
          <div>© {new Date().getFullYear()} {CONTROLLER}. All rights reserved.</div>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-white">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <h2 className="font-display text-xl sm:text-2xl font-extrabold">{title}</h2>
      <div className="text-sm text-muted-foreground space-y-3 leading-relaxed">
        {children}
      </div>
    </section>
  );
}
